import type { LabelGroups } from '../util';

import { ChatIcon, FlagIcon, TimerIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api } from '../../../api';
import { DateWithTooltip, Dropdown, Text } from '../../../components';
import { Color, Style } from '../../../constants';
import { useMyUserClient } from '../../../hooks';
import { Model } from '../../../models';
import { formatTimeSpent } from '../../../util';
import { findLabelGroupByName, getPriorityFlagColor, isInGroup } from '../util';
import AvatarGroup from './AvatarGroup';
import TicketBadge from './TicketBadge';
import WorkDoneDropdown from './WorkDoneDropdown';

type Props = {
  ticket: Api.Ticket.V_List;
  companySlug: string;
  labelGroups: LabelGroups;
  searchQuery?: string;
  invalidate: () => unknown;
};

function HighlightedSnippet({ snippet }: { snippet: string }) {
  const parts = snippet.split(/(\[\[.*?\]\])/g);
  return (
    <Text as="p" style={styles.snippet}>
      {parts.map((part, i) => {
        const match = part.match(/^\[\[(.*?)\]\]$/);
        if (match) {
          return (
            <html.span key={i} style={styles.snippetHighlight}>
              {match[1]}
            </html.span>
          );
        }
        return part;
      })}
    </Text>
  );
}

function HighlightedTitle({ title, query }: { title: string; query: string }) {
  if (!query) return <>{title}</>;
  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi'
  );
  const parts = title.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <html.span key={i} style={styles.snippetHighlight}>
            {part}
          </html.span>
        ) : (
          part
        )
      )}
    </>
  );
}

function MetaDivider() {
  return <html.span style={styles.metaDivider} />;
}

export default React.memo<Props>(function TicketTableRow({
  ticket,
  companySlug,
  labelGroups,
  searchQuery,
  invalidate,
}: Props) {
  const myUser = useMyUserClient();
  const detailLink = `/company/${companySlug}/tickets/${ticket.number}`;

  const statusGroup = findLabelGroupByName(labelGroups, 'Status');
  const platformGroup = findLabelGroupByName(labelGroups, 'Platform');
  const typeGroup = findLabelGroupByName(labelGroups, 'Type');
  const blockersGroup = findLabelGroupByName(labelGroups, 'Blockers');
  const resolutionGroup = findLabelGroupByName(labelGroups, 'Resolution');
  const priorityGroup = findLabelGroupByName(labelGroups, 'Priority');

  const statusLabels = statusGroup
    ? ticket.labels.filter(isInGroup(statusGroup))
    : [];
  const platformLabels = platformGroup
    ? ticket.labels.filter(isInGroup(platformGroup))
    : [];
  const typeLabels = typeGroup
    ? ticket.labels.filter(isInGroup(typeGroup))
    : [];
  const blockerLabels = blockersGroup
    ? ticket.labels.filter(isInGroup(blockersGroup))
    : [];
  const resolutionLabels = resolutionGroup
    ? ticket.labels.filter(isInGroup(resolutionGroup))
    : [];
  const priorityLabels = priorityGroup
    ? ticket.labels.filter(isInGroup(priorityGroup))
    : [];

  const totalMinutes = Model.WorkDone.sumMinutes(ticket.workDones);

  const hasComments = ticket.comments && ticket.comments.length > 0;

  const isBlocked = blockerLabels.length > 0;
  const isInfoNeeded = blockerLabels.some(
    (l) => l.name.toLowerCase() === 'info needed'
  );
  const isFullyResolved = resolutionLabels.some(
    (l) => l.name.toLowerCase() === 'resolved'
  );
  const isResolved = resolutionLabels.length > 0;
  const hasOtherResolution = isResolved && !isFullyResolved;

  return (
    <html.div
      style={[
        styles.row,
        isInfoNeeded && styles.blockedRow,
        isFullyResolved && styles.resolvedRow,
        hasOtherResolution && styles.otherResolutionRow,
      ]}
    >
      {/* No. — hidden on mobile, number shown inline with title instead */}
      <html.div style={[styles.cell, styles.cellNo]}>
        <Text typeface="body.regular" color="neutral100">
          #{ticket.number}
        </Text>
      </html.div>

      {/* Ticket */}
      <html.div style={[styles.cell, styles.cellTicket]}>
        {/* Title + snippet */}
        <html.div>
          <Link to={detailLink} {...css.props(styles.ticketLink)}>
            <Text
              typeface="body.medium"
              color="neutral100"
              style={styles.ticketTitle}
            >
              <Text style={styles.ticketNumberInline}>#{ticket.number} </Text>
              {searchQuery ? (
                <HighlightedTitle title={ticket.title} query={searchQuery} />
              ) : (
                ticket.title
              )}
            </Text>
          </Link>
          {ticket.searchSnippet && (
            <HighlightedSnippet snippet={ticket.searchSnippet} />
          )}
        </html.div>

        {/* Meta row — reorders to top on mobile via CSS order */}
        <html.div style={[Style.row, Style.alignCenter, styles.ticketMeta]}>
          {priorityLabels.length > 0 && (
            <>
              <html.span
                style={[Style.row, Style.alignCenter, styles.priorityItem]}
              >
                <FlagIcon
                  size={16}
                  weight="fill"
                  color={getPriorityFlagColor(priorityLabels[0].name)}
                />
                <Text style={styles.priorityText}>
                  {priorityLabels[0].name}
                </Text>
              </html.span>
              <MetaDivider />
            </>
          )}
          <Text
            typeface="body.small.regular"
            color="neutral60"
            style={styles.metaInline}
          >
            <DateWithTooltip timestamp={ticket.createdAt} />
          </Text>
          {hasComments && (
            <>
              <MetaDivider />
              <html.span
                style={[Style.row, Style.alignCenter, styles.metaItem]}
              >
                <ChatIcon size={16} color={color.neutral60} />
                <Text typeface="body.small.regular" color="neutral60">
                  {ticket.comments.length}
                </Text>
              </html.span>
            </>
          )}
          {myUser.agencyRole && (
            <>
              <MetaDivider />
              <Dropdown
                anchorSide="right"
                trigger={
                  <html.span
                    style={[
                      Style.row,
                      Style.alignCenter,
                      styles.metaItem,
                      styles.metaClickable,
                    ]}
                  >
                    <TimerIcon size={16} color={color.neutral60} />
                    <Text typeface="body.small.regular" color="neutral60">
                      {formatTimeSpent(totalMinutes) ?? '0m'}
                    </Text>
                  </html.span>
                }
              >
                <WorkDoneDropdown
                  defaultValues={{ ticket: `/tickets/${ticket.id}` }}
                  workDones={Model.WorkDone.filterLogged(ticket.workDones)}
                  invalidate={invalidate}
                />
              </Dropdown>
            </>
          )}
        </html.div>
      </html.div>

      {/* Type — hidden on mobile */}
      <html.div style={[styles.cell, styles.cellType]}>
        <Text typeface="body.regular" color="neutral100">
          {typeLabels.map((l) => l.name).join(', ')}
        </Text>
      </html.div>

      {/* Platform — hidden on mobile */}
      <html.div style={[styles.cell, styles.cellPlatform]}>
        <html.div
          style={[Style.row, Style.alignCenter, Style.wrap, styles.badgeGroup]}
        >
          {platformLabels.map((label) => (
            <TicketBadge
              key={label.id}
              label={label.name}
              color={label.color ? `#${label.color}` : undefined}
            />
          ))}
        </html.div>
      </html.div>

      {/* Status */}
      <html.div style={[styles.cell, styles.cellStatus]}>
        {statusLabels.map((label) => (
          <TicketBadge
            key={label.id}
            label={label.name}
            color={label.color ? `#${label.color}` : undefined}
            bordered
          />
        ))}
        {isBlocked && (
          <>
            <TicketBadge
              label="Blocked"
              textColor={Color.accent}
              bgColor={Color.accentBg}
              bordered
            />
            {blockerLabels.map((label) => (
              <Text key={label.id}>{label.name}</Text>
            ))}
          </>
        )}
        {isResolved && (
          <>
            <TicketBadge
              label="Resolved"
              textColor={Color.blue}
              bgColor={Color.blueBg}
              bordered
            />
            {resolutionLabels.map((label) => (
              <Text key={label.id}>{label.name}</Text>
            ))}
          </>
        )}
      </html.div>

      {/* Client — hidden on mobile */}
      <html.div
        style={[Style.row, Style.alignCenter, styles.cell, styles.cellClient]}
      >
        <AvatarGroup
          users={ticket.memberUsers.filter((u) => u.agencyRole === null)}
          max={3}
          size={32}
        />
      </html.div>

      {/* M10c — hidden on mobile */}
      <html.div
        style={[Style.row, Style.alignCenter, styles.cell, styles.cellM10c]}
      >
        <AvatarGroup
          users={ticket.memberUsers.filter((u) => u.agencyRole !== null)}
          max={3}
          size={32}
        />
      </html.div>
    </html.div>
  );
});

const styles = css.create({
  row: {
    display: 'flex',
    flexDirection: {
      default: 'row',
      '@media (max-width: 768px)': 'column',
    },
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    ':last-child': {
      borderBottomWidth: 0,
    },
    backgroundColor: '#fff',
    transition: 'background-color 0.15s',
    padding: {
      default: 0,
      '@media (max-width: 768px)': 16,
    },
    gap: {
      default: 0,
      '@media (max-width: 768px)': 16,
    },
    ':hover': {
      backgroundColor: '#f9f9fc',
    },
  },
  cell: {
    padding: {
      default: 16,
      '@media (max-width: 768px)': 0,
    },
  },
  cellNo: {
    width: 64,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    display: {
      default: 'block',
      '@media (max-width: 768px)': 'none',
    },
  },
  cellTicket: {
    flex: {
      default: 1,
      '@media (max-width: 768px)': 'none',
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: {
      default: 8,
      '@media (max-width: 768px)': 16,
    },
    minWidth: 0,
  },
  cellType: {
    width: 128,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '@media (max-width: 1000px)': {
      display: 'none',
    },
  },
  cellPlatform: {
    width: 121,
    flexShrink: 0,
    '@media (max-width: 1000px)': {
      display: 'none',
    },
  },
  cellStatus: {
    width: {
      default: 176,
      '@media (max-width: 768px)': 'auto',
    },
    flexShrink: {
      default: 0,
      '@media (max-width: 768px)': 1,
    },
    display: 'flex',
    flexDirection: {
      default: 'column',
      '@media (max-width: 768px)': 'row',
    },
    alignItems: {
      default: 'flex-start',
      '@media (max-width: 768px)': 'center',
    },
    flexWrap: {
      default: 'nowrap',
      '@media (max-width: 768px)': 'wrap',
    },
    gap: 6,
  },
  cellClient: {
    width: 146,
    flexShrink: 0,
    justifyContent: 'flex-start',
    '@media (max-width: 1000px)': {
      display: 'none',
    },
  },
  cellM10c: {
    width: 146,
    flexShrink: 0,
    justifyContent: 'flex-start',
    '@media (max-width: 1000px)': {
      display: 'none',
    },
  },
  snippet: {
    marginBlockStart: 2,
    marginBlockEnd: 0,
    marginInline: 0,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 1.4,
    color: color.neutral100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    display: '-webkit-box',
  },
  snippetHighlight: {
    backgroundColor: 'rgba(47, 109, 184, 0.15)',
  },
  ticketNumberInline: {
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'inline',
    },
    color: color.neutral60,
    fontWeight: '400',
  },
  priorityItem: {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
  },
  priorityText: {
    fontSize: 14,
    color: color.accent,
  },
  ticketTitle: {
    lineHeight: 'normal',
  },
  ticketLink: {
    textDecorationLine: 'none',
    ':hover': {
      color: '#0366d6',
    },
  },
  ticketMeta: {
    gap: 8,
    order: {
      default: 0,
      '@media (max-width: 768px)': -1,
    },
  },
  metaInline: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  metaItem: {
    gap: 4,
    fontSize: 14,
    color: color.neutral60,
  },
  metaClickable: {
    cursor: 'pointer',
    borderRadius: 4,
    paddingBlock: 2,
    paddingInline: 4,
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  metaDivider: {
    width: 1,
    height: 17,
    backgroundColor: color.neutral20,
    flexShrink: 0,
  },
  badgeGroup: {
    gap: 4,
  },
  blockedRow: {
    backgroundColor: '#F9ECEC',
    ':hover': {
      backgroundColor: '#F9E7E5',
    },
  },
  resolvedRow: {
    backgroundColor: '#EEF5FE',
    ':hover': {
      // keep the same
      backgroundColor: '#EEF5FE',
    },
  },
  otherResolutionRow: {
    backgroundColor: '#F5F5F5',
    ':hover': {
      backgroundColor: '#F0F0F0',
    },
  },
});
